import React from 'react';
import { motion } from 'framer-motion';

interface AnimatedSunProps {
  isSmiling: boolean;
  size?: 'normal' | 'large';
  sunlightMinutes?: number;
}

export default function AnimatedSun({ isSmiling, size = 'large', sunlightMinutes = 0 }: AnimatedSunProps) {
  const isLarge = size === 'large';
  const containerSize = isLarge ? 'w-64 h-64' : 'w-32 h-32';
  const faceSize = isLarge ? 'w-48 h-48' : 'w-24 h-24';
  
  // Determine sun's happiness level based on minutes
  const isVeryHappy = sunlightMinutes >= 15;
  const isHappy = sunlightMinutes >= 10;
  const isContent = sunlightMinutes >= 5;
  
  return (
    <div className={`relative ${containerSize} mx-auto`}>
      {/* Outer glow when happy */}
      {isSmiling && (
        <motion.div
          animate={{ 
            scale: [1, 1.1, 1],
            opacity: [0.3, 0.5, 0.3]
          }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          className={`absolute inset-0 rounded-full bg-gradient-radial from-yellow-400/30 to-transparent blur-xl`}
        />
      )}
      
      {/* Sun rays with improved animation */}
      <motion.div 
        className="absolute inset-0"
        animate={{ rotate: 360 }}
        transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
      >
        {[...Array(12)].map((_, i) => (
          <motion.div
            key={i}
            className={`absolute ${isLarge ? 'w-6 h-24' : 'w-3 h-12'} bg-gradient-to-t from-yellow-400 via-yellow-300 to-yellow-200 rounded-full`}
            animate={isSmiling ? {
              scaleY: [1, 1.1, 1],
              opacity: [0.7, 1, 0.7]
            } : {}}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              delay: i * 0.1,
              ease: 'easeInOut'
            }}
            style={{
              top: '50%',
              left: '50%',
              transform: `translate(-50%, -50%) rotate(${i * 30}deg) translateY(-${isLarge ? '80px' : '40px'})`,
              opacity: isSmiling ? 1 : 0.5,
              filter: isSmiling ? 'drop-shadow(0 0 8px rgba(250, 204, 21, 0.6))' : 'none',
            }}
          />
        ))}
      </motion.div>
      
      {/* Sun face with enhanced glow */}
      <motion.div 
        className={`absolute ${faceSize} top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-gradient-to-br from-yellow-300 via-yellow-400 to-yellow-500 rounded-full shadow-2xl flex items-center justify-center`}
        animate={isSmiling ? {
          scale: [1, 1.05, 1],
        } : {}}
        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
      >
        {/* Multi-layered glow effect */}
        {isSmiling && (
          <>
            <motion.div 
              className="absolute inset-0 rounded-full bg-yellow-400 blur-xl"
              animate={{ opacity: [0.3, 0.5, 0.3] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
            <motion.div 
              className="absolute inset-0 rounded-full bg-yellow-300 blur-2xl"
              animate={{ scale: [1, 1.2, 1], opacity: [0.2, 0.4, 0.2] }}
              transition={{ duration: 3, repeat: Infinity }}
            />
          </>
        )}
        
        {/* Face */}
        <div className="relative z-10 w-full h-full flex flex-col items-center justify-center">
          {/* Eyes */}
          <div className={`flex gap-${isLarge ? '12' : '6'} mb-${isLarge ? '4' : '2'}`}>
            {/* Left eye */}
            <motion.div 
              className={`${isLarge ? 'w-8 h-8' : 'w-4 h-4'} bg-amber-900 rounded-full flex items-center justify-center`}
              animate={isSmiling ? {
                scaleY: [1, 0.1, 1],
              } : {}}
              transition={{
                duration: 0.2,
                repeat: Infinity,
                repeatDelay: 3,
              }}
            >
              {isVeryHappy && (
                <motion.div 
                  className={`${isLarge ? 'w-3 h-3' : 'w-2 h-2'} bg-white rounded-full`}
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 1 }}
                />
              )}
            </motion.div>
            {/* Right eye */}
            <motion.div 
              className={`${isLarge ? 'w-8 h-8' : 'w-4 h-4'} bg-amber-900 rounded-full flex items-center justify-center`}
              animate={isSmiling ? {
                scaleY: [1, 0.1, 1],
              } : {}}
              transition={{
                duration: 0.2,
                repeat: Infinity,
                repeatDelay: 3,
              }}
            >
              {isVeryHappy && (
                <motion.div 
                  className={`${isLarge ? 'w-3 h-3' : 'w-2 h-2'} bg-white rounded-full`}
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 1 }}
                />
              )}
            </motion.div>
          </div>
          
          {/* Mouth - changes based on sunlight minutes */}
          <div className={`${isLarge ? 'mt-4' : 'mt-2'}`}>
            {!isSmiling ? (
              // Neutral mouth
              <div className={`${isLarge ? 'w-16 h-2' : 'w-8 h-1'} bg-amber-900 rounded-full`} />
            ) : isVeryHappy ? (
              // Big happy smile with animation
              <motion.svg 
                className={`${isLarge ? 'w-24 h-12' : 'w-12 h-6'}`} 
                viewBox="0 0 100 50"
                animate={{ scaleY: [1, 1.1, 1] }}
                transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 2 }}
              >
                <path
                  d="M 10 20 Q 50 50, 90 20"
                  stroke="hsl(182,29%,26%)"
                  strokeWidth="6"
                  fill="none"
                  strokeLinecap="round"
                />
              </motion.svg>
            ) : isHappy ? (
              // Medium smile
              <svg className={`${isLarge ? 'w-20 h-10' : 'w-10 h-5'}`} viewBox="0 0 100 50">
                <path
                  d="M 15 25 Q 50 45, 85 25"
                  stroke="hsl(182,29%,26%)"
                  strokeWidth="5"
                  fill="none"
                  strokeLinecap="round"
                />
              </svg>
            ) : (
              // Small smile
              <svg className={`${isLarge ? 'w-16 h-8' : 'w-8 h-4'}`} viewBox="0 0 100 50">
                <path
                  d="M 20 30 Q 50 40, 80 30"
                  stroke="hsl(182,29%,26%)"
                  strokeWidth="4"
                  fill="none"
                  strokeLinecap="round"
                />
              </svg>
            )}
          </div>
          
          {/* Rosy cheeks when very happy */}
          {isVeryHappy && (
            <>
              <motion.div 
                className={`absolute ${isLarge ? 'w-12 h-12 left-4' : 'w-6 h-6 left-2'} top-1/2 bg-rose-400 rounded-full blur-sm`}
                animate={{ opacity: [0.3, 0.5, 0.3] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              />
              <motion.div 
                className={`absolute ${isLarge ? 'w-12 h-12 right-4' : 'w-6 h-6 right-2'} top-1/2 bg-rose-400 rounded-full blur-sm`}
                animate={{ opacity: [0.3, 0.5, 0.3] }}
                transition={{ duration: 1.5, repeat: Infinity, delay: 0.5 }}
              />
            </>
          )}
        </div>
      </motion.div>
      
      {/* Sparkles when smiling */}
      {isSmiling && (
        <>
          <motion.div
            className={`absolute ${isLarge ? 'top-4 right-8' : 'top-2 right-4'} text-yellow-300 ${isLarge ? 'text-4xl' : 'text-2xl'}`}
            animate={{ 
              scale: [0, 1, 0],
              rotate: [0, 180, 360],
              opacity: [0, 1, 0]
            }}
            transition={{ duration: 2, repeat: Infinity, delay: 0 }}
          >
            ✨
          </motion.div>
          <motion.div
            className={`absolute ${isLarge ? 'bottom-8 left-4' : 'bottom-4 left-2'} text-yellow-300 ${isLarge ? 'text-3xl' : 'text-xl'}`}
            animate={{ 
              scale: [0, 1, 0],
              rotate: [0, -180, -360],
              opacity: [0, 1, 0]
            }}
            transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
          >
            ⭐
          </motion.div>
          <motion.div
            className={`absolute ${isLarge ? 'top-12 left-8' : 'top-6 left-4'} text-yellow-300 ${isLarge ? 'text-2xl' : 'text-lg'}`}
            animate={{ 
              scale: [0, 1, 0],
              rotate: [0, 180, 360],
              opacity: [0, 1, 0]
            }}
            transition={{ duration: 2, repeat: Infinity, delay: 1 }}
          >
            ✨
          </motion.div>
        </>
      )}
      
      {/* Floating particles for very happy state */}
      {isVeryHappy && (
        <>
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-yellow-300 rounded-full"
              style={{
                left: `${20 + (i * 12)}%`,
                bottom: '20%'
              }}
              animate={{
                y: [-20, -60, -20],
                opacity: [0, 1, 0],
                scale: [0.5, 1, 0.5]
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                delay: i * 0.3,
                ease: 'easeInOut'
              }}
            />
          ))}
        </>
      )}
    </div>
  );
}
